<template>
  <div class="mc-liveview__map" :class="{ 'detail-panel-open': selectedDetail }" data-testid="leaflet-technician">
    <div class="mc-map-wrapper">
      <!-- Toggle sidebar floating button (muncul saat sidebar tertutup) -->
      <transition name="fade">
        <button v-show="!sidebarOpen" type="button" class="mc-map-fab" aria-label="Buka panel teknisi"
          data-testid="open-sidebar-btn" @click="toggleSidebar">
          <i class="fas fa-users" aria-hidden="true"></i>
          <span class="mc-map-fab__label">Daftar Teknisi</span>
        </button>
      </transition>

      <!-- Map legend -->
      <div class="mc-map-legend" data-testid="map-legend">
        <div class="mc-map-legend__item">
          <span class="status-dot status-dot--busy"></span>
          <span>{{ $t('location.sideBar.status.busy') }}</span>
        </div>
        <div class="mc-map-legend__item">
          <span class="status-dot status-dot--available"></span>
          <span>{{ $t('location.sideBar.status.available') }}</span>
        </div>
      </div>

      <!-- TOGGLE LAYER (kiri atas, di bawah zoom control) -->
      <div class="mc-layer-toggle-group">
        <div class="mc-toggle-row">
          <span class="toggle-label">Pengerjaan WO</span>
          <McToggle v-model="woLayerActiveLocal" @change="toggleWOLayer" />
        </div>
        <div class="mc-toggle-row">
          <span class="toggle-label">Lokasi Teknisi</span>
          <McToggle v-model="homeLayerActiveLocal" @change="toggleHomeLayer" />
        </div>
      </div>

      <l-map ref="UserMap" :center="initialCenter" :zoom="initialZoom" :options="{ zoomControl: true, minZoom: 3 }"
        class="mc-map" @ready="onMapReady">
        <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="© OpenStreetMap contributors" />

        <!-- === LAYER PENGERJAAN WO (cluster) === -->
        <v-marker-cluster ref="clusterGroup" v-if="woLayerActiveLocal && clusterEnabled"
          :options="{ disableClusteringAtZoom: 18 }">
          <l-marker v-for="user in filteredWOLocation" :ref="`marker-${user.user_id}`" :key="`wo-${user.user_id}`"
            :lat-lng="getWOLatLng(user)" :icon="getCustomIcon(user)" @click="onMarkerClick(user)"
            :options="{ userId: user.user_id }" />
        </v-marker-cluster>

        <!-- Tanpa cluster -->
        <template v-if="woLayerActiveLocal && !clusterEnabled">
          <l-marker v-for="user in filteredWOLocation" :ref="`marker-${user.user_id}`" :key="`wo-${user.user_id}`"
            :lat-lng="getWOLatLng(user)" :icon="getCustomIcon(user)" @click="onMarkerClick(user)"
            :options="{ userId: user.user_id }" />
        </template>

        <!-- LAYER LOKASI TEKNISI (Lingkaran + Marker di tengah) -->
        <template v-if="homeLayerActiveLocal">
          <div v-for="user in filteredHomeLocation" :key="`home-${user.user_id}`" style="display: contents;">
            <l-circle :lat-lng="getHomeLatLng(user)" :radius="user.address.radius * 1000"
              :color="getHomeCircleColor(user)" :fillColor="getHomeCircleColor(user)" :fillOpacity="0.1" :weight="1"
              :opacity="0.4" :interactive="false" />
            <l-marker :lat-lng="getHomeLatLng(user)" :icon="getHomePinIcon(user)" :interactive="false" />
          </div>
        </template>
      </l-map>

      <!-- Panel detail teknisi (muncul dari sisi sidebar) -->
      <transition name="slide-panel">
        <TechnicianDetailPanel v-if="selectedDetail" :detail="selectedDetail" :visible-fields="visibleFields"
          :generate-link="generateLink" @close="CloseInfo" @show-clock-in="showModalClockIn" @open-gmap="openInGMap"
          @share="share" />
      </transition>

      <ModalClockInPhoto v-if="clockInData" :clockInData="clockInData" />
    </div>
  </div>
</template>

<script>
import L from 'leaflet'
import { LMap, LTileLayer, LMarker, LCircle } from 'vue2-leaflet'
import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import { Helper } from '@/mixin/helper'
import responsive from '@/mixin/responsive'
import ModalClockInPhoto from '@/components/Modals/ModalClockInPhoto'
import TechnicianDetailPanel from './TechnicianDetailPanel.vue'
import McToggle from '@/components/McToggle' // sesuaikan path
import { mapState } from 'vuex'

export default {
  name: 'LeafletTechnician',
  mixins: [Helper, responsive],
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LCircle,
    'v-marker-cluster': Vue2LeafletMarkerCluster,
    ModalClockInPhoto,
    TechnicianDetailPanel,
    McToggle,
  },

  data() {
    return {
      mapObject: null,
      selectedDetail: null,
      clockInData: null,
      internalUpdate: false,
      lastClickedMarker: null,   // 🆕 simpan marker yang terakhir di-klik
      isCentering: false,     // 🔹 baru
      moveEndHandler: null,   // untuk menyimpan reference handler
      activeHighlightUserId: null,      // ID user yang sedang di-highlight
      highlightWatcherInterval: null,   // interval untuk menjaga highlight
      woLayerActiveLocal: true,
      homeLayerActiveLocal: false,
      markerInstances: new Map(), // untuk menyimpan instance L.Marker per user_id
    }
  },

  computed: {
    ...mapState('sidebar', ['sidebarOpen']),
    ...mapState('users', ['technicians', 'userCluster']),
    ...mapState('map', ['mapCenter']),
    ...mapState('map', {
      mapCenterState: state => state.center,
      mapZoomState: state => state.zoom,
      woLayerActiveStore: state => state.woLayerActive,
      homeLayerActiveStore: state => state.homeLayerActive,
    }),
    selectedTechnicianFromStore() {
      return this.$store.state.map.selectedTechnician;
    },
    sidebarOpen() {
      return this.$store.state.sidebar.sideBarOpen
    },
    clusterEnabled() {
      return this.userCluster === true
    },

    // Marker teknisi — koordinat dari root latitude/longitude
    usersWithWOLocation() {
      return this.technicians.filter(t => {
        const lat = parseFloat(t.latitude)
        const lng = parseFloat(t.longitude)
        return !isNaN(lat) && !isNaN(lng)
      })
    },

    usersWithHomeLocation() {
      const result = this.technicians.filter(t => {
        if (!t.address) return false;
        const lat = parseFloat(t.address.latitude);
        const lng = parseFloat(t.address.longitude);
        const radius = parseFloat(t.address.radius);
        return !isNaN(lat) && !isNaN(lng) && radius > 0;
      });
      // Hapus baris berikut setelah selesai debugging
      return result;
    },

    mapCenter() {
      const c = this.$store.state.map.mapCenter;
      return c ? [c.lat, c.lng] : [-6.2, 106.8]; // default center
    },

    mapZoom() {
      return this.$store.state.map.mapCenter?.zoom || 10;
    },

    visibleFields() {
      return this.workOrder
    },

    workOrder() {
      return [
        { key: 'wo_number', label: this.$t('workOrder.table.woNumber'), sortable: true },
        { key: 'customer', label: this.$t('workOrder.table.customer'), sortable: true },
        { key: 'plan_date', label: this.$t('workOrder.table.processingDate'), sortable: true },
        { key: 'type_wo', label: this.$t('workOrder.table.woType'), sortable: true },
        { key: 'state', label: this.$t('workOrder.table.woStatusErp'), sortable: true },
      ]
    },
    // Nilai statis — posisi map dikelola sepenuhnya via setView() di JS
    initialCenter() { return [-6.2, 106.8]; },
    initialZoom() { return 6; },

    filteredWOLocation() {
      const selected = this.selectedTechnicianFromStore;
      if (!selected) return this.usersWithWOLocation;
      return this.usersWithWOLocation.filter(t => t.user_id === selected.user_id);
    },

    filteredHomeLocation() {
      const selected = this.selectedTechnicianFromStore;
      let list = this.usersWithHomeLocation;
      if (selected) {
        list = list.filter(t => t.user_id === selected.user_id);
      }
      return list.filter(t => {
        const lat = parseFloat(t.address.latitude);
        const lng = parseFloat(t.address.longitude);
        const radius = parseFloat(t.address.radius);
        return !isNaN(lat) && !isNaN(lng) && radius > 0 && (lat !== 0 || lng !== 0);
      });
    }
  },

  watch: {
    // mapCenter watcher dihapus — posisi map dikelola via centerAndHighlight()
    selectedTechnicianFromStore(newTech, oldTech) {
      if (!newTech) {
        this.clearHighlight();
        this.selectedDetail = null;
        return;
      }

      this.selectedDetail = newTech;

      const isSameUser = oldTech && oldTech.user_id === newTech.user_id;
      if (isSameUser && this.isAlreadyCentered(newTech)) {
        this.$nextTick(() => this.applyHighlightToCurrentUser());
        return;
      }

      // Batalkan centering sebelumnya jika masih berjalan
      if (this.moveEndHandler && this.mapObject) {
        this.mapObject.off('moveend', this.moveEndHandler);
        this.moveEndHandler = null;
        this.isCentering = false;
      }

      this.$nextTick(() => this.centerAndHighlight(newTech));
    },
  },

  beforeDestroy() {
    if (this.mapObject) {
      this.mapObject.off('moveend');
    }
    this.stopHighlightWatcher();
    // Reset selected technician agar panel tidak terbuka saat kembali ke halaman
    this.$store.commit('map/SET_SELECTED_TECHNICIAN', null);
  },

  methods: {
    // Hanya perlu method ini untuk highlight
    async applyHighlightToCurrentUser() {
      const technician = this.$store.state.map.selectedTechnician;
      if (!technician) return;
      const marker = this.findMarkerByUserId(technician.user_id);
      if (!marker) return;

      if (this.lastHighlightedMarker && this.lastHighlightedMarker !== marker) {
        this.removeHighlightFromMarker(this.lastHighlightedMarker);
      }

      const clusterGroup = this.$refs.clusterGroup?.mapObject;
      if (clusterGroup && clusterGroup.getVisibleParent && clusterGroup.getVisibleParent(marker)) {
        clusterGroup.zoomToShowLayer(marker, () => {
          this._addHighlightClass(marker);
        });
      } else {
        this._addHighlightClass(marker);
      }
    },

    _addHighlightClass(marker) {
      if (!marker._icon) return;
      marker.setZIndexOffset(999999);
      if (marker.bringToFront) marker.bringToFront();
      marker._icon.classList.add('marker-highlighted-active');
      this.lastHighlightedMarker = marker;
    },

    removeHighlightFromMarker(marker) {
      if (!marker) return;
      marker.setZIndexOffset(0);
      if (marker._icon) marker._icon.classList.remove('marker-highlighted-active');
    },

    findMarkerByUserId(userId) {
      const clusterGroup = this.$refs.clusterGroup?.mapObject;
      if (!clusterGroup) return null;
      let found = null;
      clusterGroup.eachLayer((layer) => {
        if (layer.options?.userId === userId && !layer.getChildCount) found = layer;
        if (!found && layer.getChildCount) {
          layer.getAllChildMarkers().forEach(child => {
            if (child.options?.userId === userId) found = child;
          });
        }
      });
      return found;
    },

    onMarkerReady(markerComponent, userId) {
      this.markerInstances.set(userId, markerComponent);
      const leafletMarker = markerComponent.mapObject;
      if (leafletMarker && !leafletMarker._originalPane) {
        leafletMarker._originalPane = leafletMarker.options.pane || 'markerPane';
      }
    },

    // Ikon pin kecil di tengah lingkaran (menggunakan gambar pin existing)
    getHomePinIcon(user) {
      const status = user.status || 'A';
      // Warna latar pin
      const bgColor = status === 'B'
        ? '#dc2626'   // merah = sibuk
        : status === 'A'
          ? '#16a34a' // hijau = tersedia
          : '#94a3b8'; // abu = unknown / belum install
      const initial = (user.name || '?').charAt(0).toUpperCase();

      return L.divIcon({
        html: `
      <div class="pin-marker-container" style="position: relative; width: 30px; height: 40px; text-align: center;">
        <svg width="30" height="40" viewBox="0 0 30 40" style="display: block;">
          <path d="M15 0 C6.716 0 0 6.716 0 15 C0 21.5 7.5 29 15 40 C22.5 29 30 21.5 30 15 C30 6.716 23.284 0 15 0 Z" fill="${bgColor}" stroke="#ffffff" stroke-width="2"/>
        </svg>
        <div style="position: absolute; top: 7px; left: 0; right: 0; color: white; font-weight: bold; font-size: 13px; line-height: 1; text-shadow: 0 0 2px rgba(0,0,0,0.5);">${initial}</div>
      </div>
    `,
        className: 'custom-home-pin',
        iconSize: [30, 40],
        iconAnchor: [15, 40], // ujung pin tepat di koordinat
        popupAnchor: [0, -40],
      });
    },

    resolveLeafletMarker(userId) {
      const ref = this.$refs[`marker-${userId}`];
      const comp = Array.isArray(ref) ? ref[0] : ref;
      if (comp && comp.mapObject) {
        const marker = comp.mapObject;
        // Pastikan marker sudah ditambahkan ke map (bukan hanya dalam cluster)
        if (this.mapObject && marker._icon) return marker;
      }
      return null;
    },

    // Tambahkan helper resolveLeafletMarkerComponent (sudah ada, pastikan berfungsi untuk clustered markers)
    resolveLeafletMarkerComponent(userId) {
      // Coba dari $refs
      const ref = this.$refs[`marker-${userId}`];
      let comp = Array.isArray(ref) ? ref[0] : ref;
      if (comp && comp.mapObject) return comp;
      // Alternatif: cari di markerInstances Map yang diisi saat onMarkerReady
      return this.markerInstances.get(userId) || null;
    },

    _removeHighlightFromMarker(marker) {
      if (!marker) return;
      marker.setZIndexOffset(0);
      // Kembalikan ikon asli jika ada
      if (marker._originalIcon) {
        marker.setIcon(marker._originalIcon);
        marker._originalIcon = null;
      }
      const iconElem = marker._icon;
      if (iconElem) {
        iconElem.classList.remove('marker-highlighted');
        iconElem.style.filter = '';
        iconElem.style.zIndex = '';
      }
      // Tidak perlu removeLayer/addLayer karena akan mengganggu posisi
    },

    startHighlightWatcher() {
      if (this.highlightWatcherInterval) return;

      // Setiap 500ms, coba terapkan highlight ulang
      this.highlightWatcherInterval = setInterval(() => {
        // Hanya jalankan jika ada selectedTechnician dan map sudah siap
        if (this.$store.state.map.selectedTechnician && this.mapObject) {
          this.applyHighlightToCurrentUser();
        }
      }, 500);
    },

    stopHighlightWatcher() {
      if (this.highlightWatcherInterval) {
        clearInterval(this.highlightWatcherInterval);
        this.highlightWatcherInterval = null;
      }
    },

    onMarkerClick(gmp) {
      this.$store.commit('map/SET_SELECTED_TECHNICIAN', gmp);
      // Highlight langsung — marker sudah ada di DOM karena baru diklik
      this.$nextTick(() => this.applyHighlightToCurrentUser());
    },

    // highlightTechnicianMarker bisa dihapus atau diarahkan ke applyHighlightToCurrentUser
    highlightTechnicianMarker() {
      this.applyHighlightToCurrentUser();
    },

    toggleWOLayer() {
      this.$store.commit('map/SET_WO_LAYER', this.woLayerActiveLocal)
    },

    toggleHomeLayer() {
      this.$store.commit('map/SET_HOME_LAYER_ACTIVE', this.homeLayerActiveLocal);
    },

    onMapReady(map) {
      this.mapObject = map;

      this.$nextTick(() => {
        // Naikkan z-index markerPane agar marker tidak tertutup elemen lain
        const markerPane = map.getPane('markerPane');
        if (markerPane) markerPane.style.zIndex = '650';

        setTimeout(() => map.invalidateSize(), 150);
        this.startHighlightWatcher();

        if (this.selectedTechnicianFromStore) {
          this.selectedDetail = this.selectedTechnicianFromStore;
          this.centerAndHighlight(this.selectedTechnicianFromStore);
        }
      });
    },

    isAlreadyCentered(technician) {
      if (!this.mapObject) return false;
      const currentZoom = this.mapObject.getZoom();
      const currentCenter = this.mapObject.getCenter();
      // Fallback ke address jika root lat/lng tidak tersedia
      let targetLat = technician.latitude ?? technician.lat;
      let targetLng = technician.longitude ?? technician.lng;
      if ((targetLat == null || isNaN(parseFloat(targetLat))) && technician.address) {
        targetLat = technician.address.latitude;
        targetLng = technician.address.longitude;
      }
      targetLat = parseFloat(targetLat);
      targetLng = parseFloat(targetLng);
      if (isNaN(targetLat) || isNaN(targetLng)) return false;
      const distance = currentCenter.distanceTo(L.latLng(targetLat, targetLng));
      return currentZoom >= 17 && distance < 100; // toleransi 100 meter
    },

    getLeafletMarker(gmp) {
      const ref = this.$refs[`marker-${gmp.user_id || ''}`];
      const comp = Array.isArray(ref) ? ref[0] : ref;
      return comp?.mapObject; // instance L.Marker
    },

    invalidateMap() {
      this.mapObject?.invalidateSize();
    },

    toggleSidebar() {
      this.$store.commit('sidebar/toggleSideBar')
    },

    // Helper untuk mendapatkan koordinat WO
    getWOLatLng(user) {
      return [parseFloat(user.latitude), parseFloat(user.longitude)]
    },

    // Helper untuk mendapatkan koordinat rumah
    getHomeLatLng(user) {
      return [parseFloat(user.address.latitude), parseFloat(user.address.longitude)]
    },

    // Ikon untuk marker Pengerjaan WO (beda warna biru)
    getWOMarkerIcon() {
      return L.divIcon({
        html: `<div class="marker-pin marker-pin--wo"><i class="fas fa-hard-hat"></i></div>`,
        className: 'custom-leaflet-marker wo-marker',
        iconSize: [36, 36],
        iconAnchor: [18, 36],
        popupAnchor: [0, -36],
      })
    },

    getCustomIcon(gmp) {
      const status = gmp.status || 'A'
      let statusClass = '';
      let iconUrl = '';

      if (status === 'B') {
        statusClass = 'busy';
        iconUrl = require('@/assets/images/caricon/user-red.svg');
      } else if (status === 'A') {
        statusClass = 'available';
        iconUrl = require('@/assets/images/caricon/user-green.svg');
      } else {
        statusClass = 'niy';
        iconUrl = require('@/assets/images/caricon/user-green.svg'); // atau icon default
      }

      let labelHtml = ''
      if (this.$store.state.users.markerPlate === true) {
        const labelBg = this.$store.state.users.markerLabelLayout
          ? 'bg-white bottom-icon-shape'
          : 'bg-transparent'
        labelHtml = `<div class="${labelBg} rounded marker-label"><strong>${gmp.name}</strong></div>`
      }

      const html = `
    <div class="marker-wrap">
      ${labelHtml}
      <div class="marker-pin marker-pin--${statusClass}">
        <img src="${iconUrl}" alt="${gmp.name}" />
      </div>
    </div>
  `
      return L.divIcon({
        html,
        className: 'custom-leaflet-marker',
        iconSize: [36, 50],
        iconAnchor: [18, 50],
        popupAnchor: [0, -50],
      })
    },

    // Warna lingkaran berdasarkan status teknisi
    getHomeCircleColor(user) {
      const status = user.status || 'A'
      if (status === 'B') return '#dc2626' // merah = sibuk
      if (status === 'A') return '#16a34a' // hijau = tersedia
      return '#94a3b8' // abu = belum install / unknown
    },

    // Ikon untuk marker Lokasi Teknisi (mengikuti status busy/available)
    getHomeMarkerIcon(user) {
      const status = user.status || 'A'
      const iconUrl = status === 'B'
        ? require('@/assets/images/caricon/user-red.svg')
        : require('@/assets/images/caricon/user-green.svg')
      return L.divIcon({
        html: `<div class="marker-wrap"><div class="marker-pin marker-pin--${status === 'B' ? 'busy' : 'available'}"><img src="${iconUrl}" alt="marker" /></div></div>`,
        className: 'custom-leaflet-marker',
        iconSize: [36, 50],
        iconAnchor: [18, 50],
        popupAnchor: [0, -50],
      })
    },

    async waitForMarkerAndHighlight(userId) {
      for (let attempt = 0; attempt < 40; attempt++) {
        const marker = this.resolveLeafletMarker(userId);
        if (marker && marker._icon && marker._icon.isConnected) {
          this.applyHighlightToCurrentUser();
          return;
        }
        await new Promise(r => setTimeout(r, 100));
      }
      console.warn(`Marker ${userId} tidak ditemukan setelah 4 detik`);
    },

    resetMarkerHighlight(marker) {
      if (!marker) return;

      // Kembalikan ikon asli jika ada
      if (marker._originalIcon) {
        marker.setIcon(marker._originalIcon);
        marker._originalIcon = null;
      }

      // Reset z-index
      marker.setZIndexOffset(0);

      // Hapus class highlight dari elemen DOM
      const iconElem = marker._icon;
      if (iconElem) {
        iconElem.classList.remove('marker-highlighted-active');
        iconElem.style.filter = '';
        iconElem.style.zIndex = '';
        const pin = iconElem.querySelector('.marker-pin');
        if (pin) {
          pin.style.transform = '';
          pin.style.boxShadow = '';
        }
      }

      // Hentikan maintenance interval
      if (this.highlightInterval) {
        clearInterval(this.highlightInterval);
        this.highlightInterval = null;
      }
    },

    // Pastikan saat CloseInfo, kita panggil resetHighlight
    CloseInfo() {
      if (this.lastHighlightedMarker) {
        this.resetMarkerHighlight(this.lastHighlightedMarker);
        this.lastHighlightedMarker = null;
      }
      this.selectedDetail = null;
      this.$store.commit('map/SET_SELECTED_TECHNICIAN', null);
      if (this.mapObject) this.mapObject.closePopup();
    },

    async centerAndHighlight(technician) {
      if (!this.mapObject) return;

      const latLngs = [];

      // ✅ Gunakan nilai lokal (terikat toggle UI), bukan dari store
      const woActive = this.woLayerActiveLocal;
      const homeActive = this.homeLayerActiveLocal;

      // 1. Titik WO (Pengerjaan)
      if (woActive) {
        let woLat = parseFloat(technician.latitude ?? technician.lat);
        let woLng = parseFloat(technician.longitude ?? technician.lng);
        if (!isNaN(woLat) && !isNaN(woLng)) {
          latLngs.push([woLat, woLng]);
        }
      }

      // 2. Titik Rumah (Lokasi Teknisi)
      if (homeActive && technician.address) {
        let homeLat = parseFloat(technician.address.latitude);
        let homeLng = parseFloat(technician.address.longitude);
        let radius = parseFloat(technician.address.radius);
        if (!isNaN(homeLat) && !isNaN(homeLng) && radius > 0) {
          latLngs.push([homeLat, homeLng]);
        }
      }

      if (latLngs.length === 0) {
        console.warn('Tidak ada titik yang valid untuk di-zoom');
        return;
      }

      // Zoom sesuai jumlah titik
      if (latLngs.length === 1) {
        this.mapObject.setView(latLngs[0], 18, { animate: true });
      } else {
        const bounds = L.latLngBounds(latLngs);
        this.mapObject.fitBounds(bounds, {
          padding: [50, 50],
          maxZoom: 18,
          animate: true,
        });
      }

      await new Promise(r => setTimeout(r, 300));
      this.mapObject.invalidateSize();
      await this.applyHighlightToCurrentUser();
    },

    clearHighlight() {
      if (this.lastClickedMarker) {
        this._removeHighlightFromMarker(this.lastClickedMarker);
        this.lastClickedMarker = null;
        this.activeHighlightUserId = null;
      }
    },

    showModalClockIn(data) {
      this.clockInData = {
        clock_in_photo: data.clock_in_photo,
        clock_in_address: data.clock_in_address || '-',
        wo_number: data.woNumber,
      }
      this.$bvModal.show('modal-clock-in')
    },

    generateLink(item) {
      localStorage.setItem(item.wo_number, JSON.stringify(item))
      return `/work-order/validation/form/${item.wo_number}`
    },

    openInGMap() {
      if (!this.selectedDetail) return
      const { latitude, longitude } = this.selectedDetail
      window.open(
        `http://maps.google.com/maps?&z=15&mrt=yp&t=m&q=${latitude}+${longitude}`,
        '_blank'
      )
    },

    share(param) {
      if (!this.selectedDetail) return
      const { latitude, longitude, name } = this.selectedDetail
      const url = `http://maps.google.com/maps?&z=15&mrt=yp&t=m&q=${latitude}+${longitude}`
      if (param === 'wa') {
        const text = `_Lokasi dari teknisi *${name}*_ ${url}`
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank')
      } else if (param === 'copy') {
        navigator.clipboard.writeText(url)
        this.$toast.info('Tautan tersalin', { position: 'top-center', timeout: 3000 })
      }
    },

    setModal(row) {
      this.$store.dispatch('users/setHistory', {
        name: row.fullname,
        woType: row.woType,
        loggedTime: row.loggedTime,
        woNumber: row.woNumber,
        latitude: row.latitude,
        longitude: row.longitude,
      })
      this.$store.dispatch('map/modalUserHistory', true)
      if (this.mediaQuery.matches) {
        this.$store.commit('sidebar/toggleSideBar')
      }
    },
  },
}
</script>

<style scoped>
.mc-liveview__map {
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

.mc-liveview__map.detail-panel-open {
  transition: width 0.2s ease;
}

@media (max-width: 768px) {
  .mc-liveview__map.detail-panel-open {
    margin-right: 0;
  }
}

.mc-liveview__map.panel-open {
  margin-right: 340px;
}

.mc-map-wrapper {
  position: relative;
  height: 100%;
  width: 100%;
}

.mc-map {
  height: 100%;
  width: 100%;
  z-index: 0;
}

/* FAB toggle sidebar */
.mc-map-fab {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 500;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #2563eb;
  color: #ffffff;
  border: none;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.35);
  transition: background 0.2s ease, transform 0.2s ease;
}

.mc-map-fab:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
}

.mc-map-fab__label {
  white-space: nowrap;
}

@media (max-width: 480px) {
  .mc-map-fab__label {
    display: none;
  }

  .mc-map-fab {
    padding: 12px;
    border-radius: 50%;
  }
}

/* Legend */
.mc-map-legend {
  position: absolute;
  bottom: 24px;
  left: 16px;
  z-index: 500;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  font-size: 0.75rem;
  color: #334155;
  display: flex;
  gap: 14px;
}

.mc-map-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-dot--busy {
  background: #dc2626;
}

.status-dot--available {
  background: #16a34a;
}

.mc-layer-toggle-group {
  position: absolute;
  top: 6px;
  /* persis di bawah zoom control (asumsi tinggi zoom ±40px) */
  left: 53px;
  /* sejajar kiri dengan zoom control */
  z-index: 600;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(4px);
  padding: 8px 12px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  border: 1px solid #e2e8f0;
  min-width: 180px;
}

.mc-toggle-row {
  display: flex;
  justify-content: space-between;
  /* label kiri, toggle kanan */
  align-items: center;
  gap: 16px;
  margin: 6px 0;
}

.toggle-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #1e293b;
  white-space: nowrap;
}

/* Marker WO */
.marker-pin--wo {
  width: 36px;
  height: 36px;
  background: #2563eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  border: 2px solid white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

/* Responsive */
@media (max-width: 768px) {
  .mc-layer-toggle-group {
    top: 50px;
    left: 8px;
    padding: 6px 10px;
    min-width: 150px;
  }

  .toggle-label {
    font-size: 0.75rem;
  }
}

/* Marker styles - global agar inject ke divIcon */
.custom-leaflet-marker .marker-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.custom-leaflet-marker .marker-label {
  padding: 2px 6px;
  font-size: 0.6875rem;
  white-space: nowrap;
  margin-bottom: 2px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

.custom-leaflet-marker .marker-pin {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  background: #ffffff;
  /* fallback */
}

.custom-leaflet-marker .marker-pin img {
  width: 24px;
  height: 24px;
  display: block;
  position: relative;
  z-index: 2;
}

.custom-leaflet-marker .marker-pin--busy {
  background: #dc2626;
  /* merah */
  border-color: #b91c1c;
}

.custom-leaflet-marker .marker-pin--available {
  background: #16a34a;
  /* hijau */
  border-color: #15803d;
}

.custom-leaflet-marker .marker-pin--niy {
  background: #94a3b8;
  /* abu-abu */
  border-color: #64748b;
}

/* highlight via class — scoped tidak efektif untuk Leaflet divIcon,
   highlight utama ditangani via inline style di applyHighlightToCurrentUser() */
.custom-leaflet-marker.marker-highlighted .marker-pin {
  transform: scale(1.35);
  box-shadow: 0 0 0 5px rgba(37, 99, 235, 0.55), 0 6px 20px rgba(37, 99, 235, 0.5) !important;
  z-index: 10000 !important;
}

.custom-leaflet-marker.marker-highlighted .marker-label {
  font-weight: 700;
  background: #ffffff !important;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
}

/* Popup styling */
.leaflet-popup-content-wrapper {
  border-radius: 12px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
}

.leaflet-popup-content {
  margin: 0;
  width: auto !important;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.custom-leaflet-marker.marker-highlighted .marker-pin {
  transform: scale(1.35) !important;
  box-shadow: 0 0 0 5px rgba(37, 99, 235, 0.55), 0 6px 20px rgba(37, 99, 235, 0.5) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}

.custom-leaflet-marker.marker-highlighted .marker-label {
  font-weight: 700 !important;
  background: #ffffff !important;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25) !important;
}

/* Gaya untuk marker yang sedang dipilih */
.marker-highlighted-active {
  animation: pulse-glow 1.2s infinite;
}

@keyframes pulse-glow {
  0% {
    filter: drop-shadow(0 0 5px #ffaa00) drop-shadow(0 0 10px #ff5500);
  }

  50% {
    filter: drop-shadow(0 0 15px #ffcc00) drop-shadow(0 0 30px #ff7700);
  }

  100% {
    filter: drop-shadow(0 0 5px #ffaa00) drop-shadow(0 0 10px #ff5500);
  }
}

.marker-highlighted-active .marker-pin {
  transform: scale(1.35);
  box-shadow: 0 0 0 4px #ffcc00, 0 0 0 8px rgba(255, 204, 0, 0.5), 0 0 20px rgba(255, 170, 0, 0.6);
  transition: all 0.2s ease;
}

.marker-highlighted-active .marker-pin img {
  filter: brightness(1.2) drop-shadow(0 0 4px white);
  transform: scale(1.1);
}

.marker-highlighted-active .marker-label {
  background: #ffcc00 !important;
  color: #1e293b !important;
  font-weight: bold !important;
  padding: 4px 12px !important;
  border-radius: 24px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
}

/* Highlight marker style */
.marker-highlight-wrap .marker-pin--highlight {
  transform: scale(1.3);
  border-width: 3px;
  box-shadow: 0 0 0 4px #ffd966, 0 0 0 8px rgba(255, 217, 102, 0.4);
  transition: all 0.2s ease;
  z-index: 10000;
}

.highlight-label {
  background: #ffcc00 !important;
  color: #1e293b !important;
  font-weight: bold !important;
  padding: 4px 12px !important;
  border-radius: 24px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
  font-size: 0.8rem !important;
  white-space: nowrap;
  margin-top: 4px;
}

/* Responsive */
@media (max-width: 768px) {
  .mc-liveview__map.panel-open {
    margin-right: 0;
    width: 100%;
  }

  .tech-detail-panel {
    width: 85%;
  }
}
</style>
